import { axiosInstance } from '.'
import { removeEmptyValues } from '../lib/utils/helpers'
import {
  PLATFORM_WITHDRAWAL_FEE,
  PLATFORM_WITHDRAWAL_FEE_CURRENCY,
} from '../pages/withdraw/constants'
import { WithdrawalService } from './apiTypes_v1'
import {
  GET_MAX_WITHDRAWAL_AMOUNT_FOR_OPPORTUNITY,
  GET_MAX_WITHDRAWAL_UNITS_FOR_OPPORTUNITY,
  ADD_WITHDRAWAL,
  UPDATE_OPPORTUNITY_WITHDRAWAL,
  GET_ACTIVE_WITHDRAWAL_REQUEST_FOR_OPPORTUNITY,
  SEARCH_WITHDRAWALS,
  GET_WITHDRAWAL_APPROVAL_DETAILS,
  GET_WITHDRAWAL_PREDICT_APPROVAL_DETAILS,
  GET_WITHDRAWAL_PREDICT_APPROVAL_DETAILS_UNITS,
} from './endpoints'
import { WithdrawalSearchParams } from './types'

export const withdrawalServices: WithdrawalService = {
  getMaxWithdrawalAmountByOpportunity: async (
    opportunityId: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return axiosInstance.get(GET_MAX_WITHDRAWAL_AMOUNT_FOR_OPPORTUNITY(opportunityId), {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getMaxWithdrawalUnitsByOpportunity: async (
    opportunityId: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return axiosInstance.get(GET_MAX_WITHDRAWAL_UNITS_FOR_OPPORTUNITY(opportunityId), {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getActiveWithdrawalRequest: async (
    investorId: string,
    opportunityId: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.get(
        GET_ACTIVE_WITHDRAWAL_REQUEST_FOR_OPPORTUNITY(investorId, opportunityId),
        { headers: headers },
      )
    } catch (error: any) {
      return error.response
    }
  },

  getWithdrawalApprovalDetails: async (
    withdrawalId: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.get(GET_WITHDRAWAL_APPROVAL_DETAILS(withdrawalId), {
        headers: headers,
      })
    } catch (error: any) {
      return error.response
    }
  },

  getWithdrawalPredictApprovalDetails: async (
    opportunityId: string,
    amount: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.get(
        GET_WITHDRAWAL_PREDICT_APPROVAL_DETAILS(opportunityId, amount),
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },

  getWithdrawalPredictApprovalDetailsByUnits: async (
    opportunityId: string,
    quantity: string,
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.get(
        GET_WITHDRAWAL_PREDICT_APPROVAL_DETAILS_UNITS(opportunityId, quantity),
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },

  postOpportunityWithdrawal: async (
    payload: {
      opportunityId: string
      investorId: string
      withdrawalAmount?: number
      withdrawalQuantity?: number
      metadata: { completedSteps: number } & Record<string, unknown>
    },
    headers: { [key: string]: string },
  ) => {
    try {
      const useQuantity =
        typeof payload.withdrawalQuantity === 'number' &&
        Number.isFinite(payload.withdrawalQuantity)

      const withdrawalBody = useQuantity
        ? { quantity: payload.withdrawalQuantity }
        : { amount: payload.withdrawalAmount }

      return await axiosInstance.post(
        ADD_WITHDRAWAL,
        {
          opportunity_id: payload?.opportunityId && parseInt(payload.opportunityId),
          investor_id: payload?.investorId && parseInt(payload.investorId),
          ...withdrawalBody,
          metadata: payload?.metadata,
          platform_fee: {
            amount: PLATFORM_WITHDRAWAL_FEE * 100,
            currency: PLATFORM_WITHDRAWAL_FEE_CURRENCY,
          },
        },
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },

  patchOpportunityWithdrawal: async (
    withdrawalRequestId: number,
    payload: {
      status?: string
      metadata: { completedSteps: number; receivedAmount?: number }
    },
    headers: { [key: string]: string },
  ) => {
    try {
      return await axiosInstance.patch(
        UPDATE_OPPORTUNITY_WITHDRAWAL(withdrawalRequestId),
        {
          ...(payload.status !== undefined ? { status: payload.status } : {}),
          metadata: payload.metadata,
        },
        {
          headers: headers,
        },
      )
    } catch (error: any) {
      return error.response
    }
  },
  searchWithdrawals: async (params: WithdrawalSearchParams, headers: { [key: string]: string }) => {
    try {
      const data = await axiosInstance.get(SEARCH_WITHDRAWALS, {
        params: removeEmptyValues(params),
        headers: headers,
      })
      return {
        ...data.data,
        withdrawals: data.data.withdrawals,
        pagination: {
          count: data.data.count,
          limit: data.data.limit,
          offset: data.data.offset,
          total: data.data.total,
        },
        status: data.status,
      }
    } catch (error: any) {
      return error.response
    }
  },
}
